import { useState, useEffect, useCallback } from 'react';
import client from '../api/client';

// Fallback defaults used only when school profile is empty or not yet loaded
export const DEFAULT_ACADEMIC_YEAR = '2026-2027';
export const DEFAULT_TERMS = ['Term 1', 'Term 2', 'Term 3', 'Term 4'];
export const DEFAULT_SETTINGS = {
  attendance_enabled: true,
  grades_enabled:     true,
  fees_enabled:       true,
  timetable_enabled:  true,
  messaging_enabled:  true,
};

/**
 * Resolve the correct school-profile endpoint for the logged-in user's role.
 *
 * - admin   → /admin/school/profile   (full document, write access elsewhere)
 * - teacher → /teacher/school/profile (read-only branding fields)
 * - parent  → /parent/school-profile   (parent-specific read-only branding endpoint)
 * - student → /admin/school/profile   (same)
 *
 * The role is read from localStorage so the hook works before React context
 * is fully hydrated.
 */
function resolveProfileEndpoint() {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.role === 'teacher') return '/teacher/school/profile';
      if (u?.role === 'parent')  return '/parent/school-profile';
      if (u?.role === 'student') return '/student/school-profile';
    }
  } catch (_) {}
  return '/admin/school/profile';
}

// Module-level cache — keyed by endpoint so admin and teacher caches are separate
const _caches = {};       // { [endpoint]: data }
const _listeners = {};    // { [endpoint]: Set<fn> }
// Bumped on every invalidation so logo <img> src includes a fresh cache-bust param
export let logoCacheBust = Date.now();

/**
 * useSchoolProfile()
 *
 * Returns { academicYear, terms, settings, profile, school, loading }
 *
 * Automatically picks the right API endpoint based on the logged-in user's role.
 * When invalidateSchoolProfileCache() is called, ALL mounted instances re-fetch.
 */
export function useSchoolProfile() {
  const endpoint = resolveProfileEndpoint();

  const [profile, setProfile] = useState(_caches[endpoint] || null);
  const [loading, setLoading] = useState(!_caches[endpoint]);

  const fetchProfile = useCallback(() => {
    setLoading(true);
    // Prefer the in-memory axios default (set per-tab by AuthContext.setAuthToken).
    // This is tab-isolated and not affected by other tabs writing to localStorage.
    // Fall back to localStorage only on first mount before AuthContext has run.
    const inMemoryToken = client.defaults.headers.common['Authorization']?.replace('Bearer ', '');
    const lsToken = localStorage.getItem('token');
    const token = inMemoryToken || lsToken;
    // Guard: if the stored user is a superadmin (SA portal pollution), skip the fetch
    // to avoid loading a different school's profile into this admin tab.
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (!inMemoryToken && u?.role === 'superadmin') {
        setLoading(false);
        return;
      }
    } catch (_) {}
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    client.get(endpoint, { headers })
      .then(({ data }) => {
        // Normalise the profile on the client side as a safety net.
        // The server already applies these defaults, but guard here too so
        // the UI never shows blank branding even if an older server build is
        // running or the network returns a partial response.
        const normalised = {
          ...data,
          // Prefer logo_url; fall back to legacy logo field
          logo_url:      data.logo_url      || data.logo      || '',
          logo:          data.logo          || data.logo_url  || '',
          primary_color: data.primary_color || '#1a3a5c',
          academic_year: data.academic_year || DEFAULT_ACADEMIC_YEAR,
          terms:         (data.terms && data.terms.length > 0) ? data.terms : DEFAULT_TERMS,
          settings:      data.settings ? { ...DEFAULT_SETTINGS, ...data.settings } : DEFAULT_SETTINGS,
        };
        _caches[endpoint] = normalised;
        setProfile(normalised);
      })
      .catch((err) => {
        // Log the error so it is visible in the browser console for debugging,
        // but do not crash — leave profile as null so the UI renders with
        // neutral fallbacks rather than hardcoded school names / colours.
        console.warn('[useSchoolProfile] Failed to load school profile from', endpoint, err?.response?.status, err?.message);
      })
      .finally(() => setLoading(false));
  }, [endpoint]);

  useEffect(() => {
    // Register this instance so invalidation can trigger a re-fetch
    if (!_listeners[endpoint]) _listeners[endpoint] = new Set();
    _listeners[endpoint].add(fetchProfile);

    if (_caches[endpoint]) {
      setProfile(_caches[endpoint]);
      setLoading(false);
    } else {
      fetchProfile();
    }

    return () => {
      _listeners[endpoint]?.delete(fetchProfile);
    };
  }, [endpoint, fetchProfile]);

  const academicYear = profile?.academic_year || DEFAULT_ACADEMIC_YEAR;
  const terms        = (profile?.terms && profile.terms.length > 0)
    ? profile.terms
    : DEFAULT_TERMS;
  const settings     = profile?.settings
    ? { ...DEFAULT_SETTINGS, ...profile.settings }
    : DEFAULT_SETTINGS;

  return { academicYear, terms, settings, profile, school: profile, loading };
}

/**
 * Invalidate the cache for a specific endpoint (or all endpoints) and notify
 * all mounted hook instances to re-fetch immediately.
 *
 * Call this after saving the school profile.
 */
export function invalidateSchoolProfileCache(endpoint) {
  logoCacheBust = Date.now(); // bump so sidebar logo src changes → browser fetches fresh
  if (endpoint) {
    delete _caches[endpoint];
    _listeners[endpoint]?.forEach(fn => fn());
  } else {
    Object.keys(_caches).forEach(ep => {
      delete _caches[ep];
      _listeners[ep]?.forEach(fn => fn());
    });
  }
}
